import React, { useState, useEffect, useRef } from 'react';
import { db, firebaseConfig, auth } from '../firebase'; 
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, setDoc, query, where } from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { FaTrash, FaUserPlus, FaGuitar, FaEdit, FaKey, FaUserShield, FaCheckCircle, FaEnvelope, FaToggleOn, FaToggleOff, FaStar } from 'react-icons/fa';

export default function Musicians() {
  const [musicians, setMusicians] = useState([]);
  const [instrumentsList, setInstrumentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados do Formulário
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [primaryRole, setPrimaryRole] = useState(''); // NOVO: Papel principal
  const [isActive, setIsActive] = useState(true);

  // Dados de Acesso
  const [hasAccess, setHasAccess] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const formTopRef = useRef(null);

  const musiciansRef = collection(db, "musicians");
  const instrumentsRef = collection(db, "instruments");
  const usersRef = collection(db, "app_users");

  // ... (loadData e handlePhoneChange mantidos iguais ao anterior) ...
  const loadData = async () => {
    setLoading(true);
    try {
      const instData = await getDocs(instrumentsRef);
      const instList = instData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      instList.sort((a, b) => a.name.localeCompare(b.name));
      setInstrumentsList(instList);

      const musData = await getDocs(musiciansRef);
      const musList = musData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      
      const usersData = await getDocs(usersRef);
      const usersMap = {};
      usersData.forEach(doc => {
        usersMap[doc.data().musicianId] = doc.data();
      });

      const mergedList = musList.map(m => ({
        ...m,
        loginData: usersMap[m.id] || null
      }));

      mergedList.sort((a, b) => {
        if (a.isActive === b.isActive) return a.name.localeCompare(b.name);
        return a.isActive ? -1 : 1;
      });

      setMusicians(mergedList);
    } catch (error) {
      console.error("Erro ao carregar:", error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 10) { value = value.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); } 
    else if (value.length > 6) { value = value.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3'); } 
    else if (value.length > 2) { value = value.replace(/^(\d{2})(\d{0,5})/, '($1) $2'); }
    setPhone(value);
  };

  // === LÓGICA ATUALIZADA DE SELEÇÃO DE FUNÇÃO ===
  const toggleRole = (roleName) => {
    if (selectedRoles.includes(roleName)) {
      const newRoles = selectedRoles.filter(r => r !== roleName);
      setSelectedRoles(newRoles);
      // Se removeu o principal, reseta o principal
      if (primaryRole === roleName) setPrimaryRole('');
    } else {
      const newRoles = [...selectedRoles, roleName];
      setSelectedRoles(newRoles);
      // Se for o primeiro adicionado, vira principal automaticamente
      if (newRoles.length === 1) setPrimaryRole(roleName);
    }
  };

  // Define função principal manualmente
  const handleSetPrimary = (e, roleName) => {
    e.stopPropagation(); // Evita clicar na div pai
    setPrimaryRole(roleName);
  };

  // ... (registerUserInFirebase, handlePasswordReset mantidos) ...
  const registerUserInFirebase = async (emailReg, passwordReg) => {
    const secondaryApp = initializeApp(firebaseConfig, "SecondaryApp");
    const secondaryAuth = getAuth(secondaryApp);
    try {
      await createUserWithEmailAndPassword(secondaryAuth, emailReg, passwordReg);
      await signOut(secondaryAuth);
      deleteApp(secondaryApp);
      return true;
    } catch (error) {
      console.error("Erro Auth:", error);
      deleteApp(secondaryApp);
      if (error.code === 'auth/email-already-in-use') return true;
      alert("Erro ao criar login no Firebase: " + error.message);
      return false;
    }
  };

  const handlePasswordReset = async () => {
    if(!email) return;
    if(window.confirm(`Enviar link de redefinição para ${email}?`)) {
      try {
        await sendPasswordResetEmail(auth, email);
        alert("E-mail enviado! Verifique a caixa de entrada.");
      } catch (error) {
        alert("Erro ao enviar: " + error.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || selectedRoles.length === 0) {
      alert("Preencha o nome e selecione instrumentos.");
      return;
    }
    // Garante que tenha um papel principal
    if (!primaryRole && selectedRoles.length > 0) {
        setPrimaryRole(selectedRoles[0]); // Força o primeiro se não tiver
    }
    const finalPrimaryRole = primaryRole || selectedRoles[0];

    if (hasAccess) {
        if (!email) { alert("Preencha o e-mail."); return; }
        if ((!editId || password.length > 0) && password.length < 6) { alert("Senha deve ter 6+ caracteres."); return; }
    }

    // Validação de Duplicidade
    if (hasAccess) {
        try {
            const q = query(usersRef, where("email", "==", email.toLowerCase()));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const existingUser = querySnapshot.docs[0].data();
                if (existingUser.musicianId !== editId) {
                    alert(`ERRO: O e-mail ${email} já está em uso por ${existingUser.musicianName}.`);
                    return;
                }
            }
        } catch (err) { console.error(err); return; }
    }

    try {
      let musicianId = editId;

      // Salva Músico com PRIMARY ROLE
      const musicianData = {
        name,
        roles: selectedRoles,
        primaryRole: finalPrimaryRole, // NOVO CAMPO
        phone,
        isActive: isActive,
        email: hasAccess ? email.toLowerCase() : '',
        updatedAt: new Date()
      };

      if (editId) {
        await updateDoc(doc(db, "musicians", editId), musicianData);
      } else {
        const docRef = await addDoc(musiciansRef, { ...musicianData, createdAt: new Date() });
        musicianId = docRef.id;
      }

      if (hasAccess) {
        if (!editId || password) {
             const authOk = await registerUserInFirebase(email, password);
             if(!authOk) return; 
        }
        await setDoc(doc(db, "app_users", email.toLowerCase()), {
          email: email.toLowerCase(),
          musicianId: musicianId,
          musicianName: name,
          isAdmin: isAdmin,
          updatedAt: new Date()
        });
        if (editId && originalEmail && originalEmail !== email) {
           await deleteDoc(doc(db, "app_users", originalEmail));
        }
      } else {
        if (editId && originalEmail) await deleteDoc(doc(db, "app_users", originalEmail));
      }
      
      handleCancel();
      loadData();
      if(!editId && hasAccess) alert("Salvo com sucesso!");
      
    } catch (error) {
      console.error("Erro ao salvar:", error);
      alert("Erro técnico: " + error.message);
    }
  };

  const handleEdit = (musician) => {
    setName(musician.name);
    setPhone(musician.phone || '');
    setIsActive(musician.isActive !== false);
    
    if (Array.isArray(musician.roles)) {
        setSelectedRoles(musician.roles);
        setPrimaryRole(musician.primaryRole || musician.roles[0]); // Carrega principal
    } else if (musician.role) {
        setSelectedRoles([musician.role]);
        setPrimaryRole(musician.role);
    } else {
        setSelectedRoles([]);
        setPrimaryRole('');
    }

    if (musician.loginData) {
        setHasAccess(true);
        setEmail(musician.loginData.email);
        setOriginalEmail(musician.loginData.email);
        setIsAdmin(musician.loginData.isAdmin);
    } else {
        setHasAccess(false); setEmail(''); setOriginalEmail(''); setIsAdmin(false);
    }
    setPassword('');
    setEditId(musician.id);
    setIsFormOpen(true);

    setTimeout(() => {
        if(formTopRef.current) formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancel = () => {
    setName(''); setPhone(''); setSelectedRoles([]); setIsActive(true); setPrimaryRole('');
    setHasAccess(false); setEmail(''); setPassword(''); setIsAdmin(false); setOriginalEmail('');
    setEditId(null); setIsFormOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, loginEmail) => {
    if (window.confirm("Tem certeza? Isso excluirá o cadastro permanentemente.")) {
      await deleteDoc(doc(db, "musicians", id));
      if(loginEmail) await deleteDoc(doc(db, "app_users", loginEmail));
      loadData();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Músicos & Equipe</h1>
        <button onClick={() => { if (isFormOpen) handleCancel(); else setIsFormOpen(true); }} className="bg-brand-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800 transition shadow">
          <FaUserPlus /> <span className="hidden sm:inline">{isFormOpen ? 'Cancelar' : 'Novo Cadastro'}</span>
        </button>
      </div>
      <div ref={formTopRef}></div>

      {isFormOpen && (
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6 border-t-4 border-brand-blue animate-fade-in">
          <h3 className="text-lg font-bold mb-4 text-gray-700">{editId ? 'Editar Dados' : 'Novo Cadastro'}</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados Básicos (Igual anterior) */}
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-bold text-gray-600 mb-1">Nome</label><input value={name} onChange={(e) => setName(e.target.value)} className="w-full border p-2 rounded outline-none focus:border-brand-blue" required /></div>
              <div><label className="block text-sm font-bold text-gray-600 mb-1">WhatsApp</label><input value={phone} onChange={handlePhoneChange} className="w-full border p-2 rounded outline-none focus:border-brand-blue" placeholder="(00) 00000-0000" /></div>
            </div>

            {/* SELEÇÃO DE FUNÇÕES E FUNÇÃO PRINCIPAL */}
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-bold text-gray-600 mb-2">Funções (Clique na Estrela p/ Principal):</label>
                    <div className="grid grid-cols-1 gap-2 bg-gray-50 p-3 rounded border max-h-40 overflow-y-auto">
                        {instrumentsList.map((inst) => {
                            const isSelected = selectedRoles.includes(inst.name);
                            const isPrimary = primaryRole === inst.name;
                            return (
                                <div key={inst.id} 
                                    className={`flex items-center justify-between p-2 rounded text-sm border transition ${isSelected ? 'bg-white border-brand-blue' : 'bg-gray-100 border-gray-200'}`}
                                >
                                    <div className="flex items-center gap-2 cursor-pointer flex-1" onClick={() => toggleRole(inst.name)}>
                                        <div className={`w-4 h-4 border rounded-sm flex items-center justify-center ${isSelected ? 'bg-brand-blue border-brand-blue' : 'bg-white'}`}>
                                            {isSelected && <div className="w-2 h-2 bg-white rounded-sm"></div>}
                                        </div>
                                        <span className={isSelected ? 'font-bold text-brand-blue' : 'text-gray-500'}>{inst.name}</span>
                                    </div>
                                    {isSelected && (
                                        <button type="button" onClick={(e) => handleSetPrimary(e, inst.name)} className={`text-lg ${isPrimary ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`}>
                                            <FaStar />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Status (Igual) */}
                <div>
                    <label className="block text-sm font-bold text-gray-600 mb-2">Status do Músico:</label>
                    <button type="button" onClick={() => setIsActive(!isActive)} className={`w-full p-4 rounded-lg border-2 shadow-sm flex items-center justify-center gap-3 transition-all transform active:scale-95 ${isActive ? 'bg-green-50 border-green-500 text-green-700 hover:bg-green-100' : 'bg-red-50 border-red-500 text-red-700 hover:bg-red-100'}`}>
                         <div className="text-2xl">{isActive ? <FaToggleOn /> : <FaToggleOff />}</div>
                         <div className="text-left"><span className="block font-bold text-lg">{isActive ? 'ATIVO' : 'INATIVO'}</span><span className="text-xs opacity-80">{isActive ? 'Disponível para escalas' : 'Não aparece nas escalas'}</span></div>
                    </button>
                </div>
            </div>

            {/* Login (Igual) */}
            <div className={`p-4 rounded border transition-colors ${hasAccess ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-3"><input type="checkbox" id="hasAccess" checked={hasAccess} onChange={e => setHasAccess(e.target.checked)} className="w-5 h-5 accent-brand-blue cursor-pointer" /><label htmlFor="hasAccess" className="font-bold text-gray-700 cursor-pointer select-none text-lg">Permitir Acesso ao App (Login)</label></div>
                {hasAccess && (
                  <div className="grid md:grid-cols-2 gap-4 animate-fade-in mt-4">
                    <div><label className="block text-xs font-bold text-gray-500 mb-1">E-mail</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue" required /></div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">{editId ? 'Alterar Senha (Opcional)' : 'Senha Inicial'}</label>
                      <div className="flex items-center border rounded bg-white focus-within:border-brand-blue"><span className="p-2 text-gray-400"><FaKey /></span><input type="text" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-2 outline-none rounded" placeholder={editId ? "Deixe vazio para manter a atual" : "Mín. 6 caracteres"} /></div>
                      {editId && (<button type="button" onClick={handlePasswordReset} className="text-xs text-blue-600 hover:underline mt-2 flex items-center gap-1 font-bold"><FaEnvelope /> Enviar link de redefinição de senha</button>)}
                    </div>
                    <div className="md:col-span-2 mt-2"><label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded border hover:bg-gray-50"><input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} className="w-5 h-5 accent-brand-gold" /><span className="text-sm font-bold text-gray-700"><FaUserShield className="inline text-brand-gold mr-1" /> Conceder Permissão de Administrador (Líder)</span></label></div>
                  </div>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              {editId && <button type="button" onClick={handleCancel} className="bg-gray-200 px-6 py-3 rounded font-bold text-gray-700 hover:bg-gray-300">Cancelar</button>}
              <button type="submit" className="bg-brand-gold text-brand-blue font-bold px-8 py-3 rounded shadow hover:opacity-90 flex-1 md:flex-none">{editId ? 'Salvar Alterações' : 'Cadastrar'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Listagem (Igual) */}
      {loading ? <div className="text-center p-10">Carregando...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-20">
          {musicians.map((musician) => (
            <div key={musician.id} className={`bg-white p-4 rounded-lg shadow flex flex-col justify-between border-t-4 transition relative overflow-hidden ${musician.isActive ? 'border-gray-100 hover:border-brand-gold' : 'border-red-200 opacity-75 grayscale-[0.5]'}`}>
              {!musician.isActive && (<div className="absolute top-0 right-0 bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded-bl">INATIVO</div>)}
              <div className="flex items-start gap-4 mb-4">
                <div className={`p-3 rounded-full h-fit ${musician.loginData?.isAdmin ? 'bg-brand-gold text-brand-blue' : 'bg-gray-100 text-brand-blue'}`}>{musician.loginData?.isAdmin ? <FaUserShield size={20} /> : <FaGuitar size={20} />}</div>
                <div className="overflow-hidden">
                  <h4 className="font-bold text-gray-800 text-lg truncate">{musician.name}</h4>
                  <div className="flex flex-wrap gap-1 mt-1 mb-2">
                    {Array.isArray(musician.roles) && musician.roles.map((role, idx) => (
                      <span key={idx} className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${role === musician.primaryRole ? 'bg-brand-blue text-white border-brand-blue' : 'bg-blue-50 text-brand-blue border-blue-100'}`}>
                        {role} {role === musician.primaryRole && '★'}
                      </span>
                    ))}
                  </div>
                  {musician.loginData ? (<div className="text-xs text-gray-500 bg-gray-50 p-1 rounded border border-gray-100 flex items-center gap-1"><FaCheckCircle className="text-green-500 flex-shrink-0"/> <span className="truncate">{musician.loginData.email}</span></div>) : (<p className="text-xs text-gray-400 italic">Acesso não configurado</p>)}
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t pt-3">
                <button onClick={() => handleEdit(musician)} className="text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded text-xs font-bold uppercase flex items-center gap-1 transition"><FaEdit /> Editar</button>
                <button onClick={() => handleDelete(musician.id, musician.loginData?.email)} className="text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1 rounded text-xs font-bold uppercase flex items-center gap-1 transition"><FaTrash /> Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}